import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { MouseEvent, useCallback, useLayoutEffect, useRef, useState } from 'react'

import { ReactComponent as CloseIcon } from 'assets/icons/close16.svg'
import Styles from './Tab.module.scss'
import { TabEntry, useTabRoot } from './TabRootStore'
import { Button } from 'components/Button'
import { clamp } from 'lodash'

type TabProps = {
  index: number
  width: number
  tabEntry: TabEntry
}

export const Tab = observer(function Tab({ index, width, tabEntry }: TabProps) {
  const tabRoot = useTabRoot()
  const tabRef = useRef<HTMLDivElement>(null)

  const [isDragged, setIsDragged] = useState(false)
  const dragOffsetX = useRef(0)
  const dragInitialMousePosX = useRef(0)

  const isActive = tabRoot.currentTab === tabEntry
  const { title } = tabEntry

  const onClose = useCallback(() => {
    tabRoot.closeTab(tabEntry)
  }, [tabRoot, tabEntry])

  const onAuxClick = useCallback(
    (ev: MouseEvent) => {
      if (ev.button === 1 /* Middle click */) {
        onClose()
      }
    },
    [onClose],
  )

  const onMouseDown = useCallback(
    (ev: MouseEvent) => {
      if (ev.button === 1 /* Middle click */) {
        ev.preventDefault() // Disable middle click scrolling
      }

      // Left click
      if (ev.button === 0) {
        tabRoot.setCurrentTab(tabEntry)

        const onMouseMove = (ev: any) => {
          if (tabRef.current == null) {
            return
          }
          tabRef.current.style.left = `${ev.clientX - dragOffsetX.current}px`
          const curIndex = tabRoot.tabs.indexOf(tabEntry)
          const newIndex = clamp(
            Math.floor((ev.clientX - dragOffsetX.current + width / 2) / width),
            0,
            tabRoot.tabs.length - 1,
          )
          if (curIndex !== newIndex) {
            tabRoot.movePosition(tabEntry, newIndex)
          }
        }

        function onMouseUp() {
          setIsDragged(false)
          window.removeEventListener('mouseup', onMouseUp)
          window.removeEventListener('mousemove', onMouseMove)
          document.body.style.pointerEvents = ''
        }

        window.addEventListener('mouseup', onMouseUp)
        window.addEventListener('mousemove', onMouseMove)
        dragOffsetX.current = ev.clientX - width * tabRoot.tabs.indexOf(tabEntry)
        dragInitialMousePosX.current = ev.clientX
        setIsDragged(true)
        document.body.style.pointerEvents = 'none'
      }
    },
    [tabEntry, tabRoot, width],
  )

  useLayoutEffect(() => {
    if (tabRef.current && tabRef.current.style.left === '') {
      tabRef.current.style.left = `${dragInitialMousePosX.current - dragOffsetX.current}px`
    }
  }, [isDragged])

  return (
    <div
      ref={tabRef}
      key={tabEntry.uuid}
      className={cx(Styles.tab, {
        [Styles.active]: isActive,
        [Styles.isDragged]: isDragged,
      })}
      onAuxClick={onAuxClick}
      onMouseDown={onMouseDown}
      style={isDragged ? { width } : { left: width * index, width }}
    >
      <span className={Styles.titleLabel}>{title}</span>
      {tabRoot.canCloseTabs && (
        <Button
          frontIcon={CloseIcon}
          size="sm"
          variant="none"
          className={Styles.titleCloseBtn}
          iconClassName={Styles.titleCloseBtnIcon}
          onPress={onClose}
        />
      )}
    </div>
  )
})
